import React, { useEffect, useState } from 'react';
import { useGlobalContext } from 'Context/GlobalContext';
import { useCedulasContext } from 'Context/CedulasPageContext';
import {
    CONTACT_INFO,
    formatLocation,
    processSenasParticulares,
    processVestimenta,
} from 'Utils/MissingPersonUtils';
import MissingPersonPng from './MissingPersonPng.jsx';
import MissingPersonCardFooter from './MissingPersonCardFooter.jsx';
import { WEB as styles, PNG_L, pngViewScale, titularFor } from './styles';
import { DISCLAIMER } from './pngCapture';
import {
    MARCA_AGUA, LOGO_COBUPEJ_CEDULA, LOGO_JAL_CEDULA, FB_ICON, X_ICON, VIEW,
} from 'Assets/index.js';
import { Box, Typography } from '@mui/material';
import { CloseOutlined } from '@ant-design/icons';
import { QRCode, Modal, Button as ButtonAntd } from 'antd';
import MissingPersonImg from '../../atoms/MissingPersonImg/MissingPersonImg.js';
import ExpandBtn from '../../atoms/ExpandBtn.jsx';

const Text = (props) => <Typography variant="inherit" component="div" {...props} />;

const WebDataRow = ({ label, value, whole }) => (
    <Box>
        <Text component="span" sx={styles.lbl}>{label}:{whole ? ' ' : '\u00a0'}</Text>
        <Text component="span" sx={[styles.val, whole && styles.whole]}>{value}</Text>
    </Box>
);

const MissingPersonCard = ({ personItem, onPressReport, onPressShare }) => {
    const { responsiveScreen } = useGlobalContext();
    const {
        hasInfoField, verifyAge, verifyStature, formatDate,
        cedulaAnalyticsEvent, searchedQR, searchedShare, handleSearchedQR,
        handleSearchedShare
    } = useCedulasContext();

    const [viewImageModal, setViewImageModal] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [viewScale, setViewScale] = useState(pngViewScale);
    const viewSize = PNG_L.size * viewScale;

    const {
        condicion_localizacion, colonia, municipio, estado, nombre_completo,
        edad_momento_desaparicion, genero, sexo, complexion, estatura,
        tez, cabello, ojos_color, fecha_desaparicion, ruta_foto,
        descripcion_sena_particular, descripcion_vestimenta, id_cedula_busqueda
    } = personItem;

    const titular = titularFor(condicion_localizacion);
    const ubicacion = formatLocation(colonia, municipio, estado);

    const vestimenta = processVestimenta(descripcion_vestimenta);
    const senas = processSenasParticulares(descripcion_sena_particular);
    const vestimentaText = vestimenta.length
        ? vestimenta.map((item) => item.formattedText).join('. ')
        : 'SIN DATOS';
    const senasText = senas.length
        ? senas.map((item) => item.formattedText).join('. ')
        : 'SIN DATOS';

    const qrValue = `${window.location.protocol}//${window.location.host}/cedulas-de-busqueda?id_cedula_busqueda=${id_cedula_busqueda}&qr=true`;

    const chip = (value, prefix = '') => (value && value !== 'SIN DATO' ? `${prefix}${value}` : null);
    const chips = [
        chip(verifyAge(edad_momento_desaparicion)),
        chip(hasInfoField(sexo)),
        chip(verifyStature(estatura)),
        chip(hasInfoField(complexion)),
        chip(hasInfoField(tez), 'TEZ '),
        chip(hasInfoField(ojos_color), 'OJOS '),
        chip(hasInfoField(cabello)),
    ].filter(Boolean);

    const handleViewImage = () => {
        setViewImageModal(true);
        cedulaAnalyticsEvent('Ver cédula de búsqueda', 'Visualiza la cedula completa y sin responsive');
    };

    useEffect(() => {
        if (searchedQR || searchedShare) {
            handleSearchedShare(false);
            handleSearchedQR(false);
            setViewImageModal(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!viewImageModal || responsiveScreen) return;
        const fit = () => setViewScale(pngViewScale());
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [viewImageModal, responsiveScreen]);

    const foto = (
        <MissingPersonImg
            format="WEB"
            ruta_foto={ruta_foto}
            alt_text={`Fotografía de ${nombre_completo}`}
        />
    );

    const lastSeen = (sx) => (
        <Box sx={sx}>
            <Text component="span" sx={styles.lbl}>Se le vio por última vez el: </Text>
            <Text component="span" sx={[styles.fecha, styles.whole]}>{formatDate(fecha_desaparicion)}</Text>
            <br />
            <Text component="span" sx={styles.lbl}>En:&nbsp;</Text>
            <Text component="span" sx={styles.val}>{ubicacion}</Text>
        </Box>
    );

    const desktopBody = (
        <Box sx={[styles.body, styles.desktop.root]}>
            <Box component="button" type="button" sx={styles.desktop.open}>
                Ver cédula completa de {nombre_completo}
            </Box>
            <Box sx={styles.desktop.left}>
                <Box sx={styles.desktop.photo}>{foto}</Box>
                <Box sx={styles.desktop.contact}>
                    <Text sx={styles.desktop.t}>¿Tienes información sobre su paradero?</Text>
                    <Text sx={styles.desktop.s}>Comunícate con nosotros:</Text>
                    <Text sx={styles.desktop.ph}>{CONTACT_INFO.PHONE}</Text>
                    <Text sx={styles.desktop.em}>{CONTACT_INFO.EMAIL}</Text>
                    <Box sx={styles.desktop.qrbox}>
                        <QRCode type="svg" bordered={false} size={72} value={qrValue} aria-label="Código QR" />
                    </Box>
                    <Box sx={styles.desktop.redes}>
                        <img src={FB_ICON} alt="Facebook" />
                        <img src={X_ICON} alt="X" />
                        <Text component="span">{CONTACT_INFO.SOCIAL_MEDIA}</Text>
                    </Box>
                </Box>
                <Box sx={styles.desktop.logos}>
                    <img src={LOGO_COBUPEJ_CEDULA} alt="Comisión de Búsqueda de Personas de Jalisco" />
                    <img src={LOGO_JAL_CEDULA} alt="Gobierno de Jalisco" />
                </Box>
            </Box>
            <Box sx={styles.desktop.right}>
                <Text sx={[styles.titular, styles.desktop.titular, { color: titular.color }]}>{titular.text}</Text>
                <Text sx={[styles.nombre, styles.desktop.nombre]}>{hasInfoField(nombre_completo)}</Text>
                {lastSeen(styles.desktop.ls)}
                <Box sx={styles.desktop.dl}>
                    <WebDataRow label="Edad al momento de la desaparición" value={verifyAge(edad_momento_desaparicion)} whole />
                    <WebDataRow label="Sexo" value={hasInfoField(sexo)} />
                    <WebDataRow label="Género" value={hasInfoField(genero)} />
                </Box>
                <Box sx={[styles.desktop.box, styles.desktop.boxF]}>
                    <Text sx={styles.lbl}>Características físicas:</Text>
                    <WebDataRow label="Estatura" value={verifyStature(estatura)} />
                    <WebDataRow label="Complexión" value={hasInfoField(complexion)} />
                    <WebDataRow label="Tez" value={hasInfoField(tez)} />
                    <WebDataRow label="Color de ojos" value={hasInfoField(ojos_color)} />
                    <WebDataRow label="Cabello" value={hasInfoField(cabello)} />
                    <Box sx={styles.desktop.spacer} />
                    <Text sx={styles.lbl}>Señas particulares:</Text>
                    <Box sx={styles.desktop.gap} />
                    <Text sx={styles.val}>{senasText}</Text>
                </Box>
                <Box sx={[styles.desktop.box, styles.desktop.boxV]}>
                    <Text sx={styles.lbl}>Vestimenta:</Text>
                    <Box sx={styles.desktop.gap} />
                    <Text sx={styles.val}>{vestimentaText}</Text>
                </Box>
                <Text sx={styles.desktop.disc}>{DISCLAIMER}</Text>
            </Box>
        </Box>
    );

    const mobileBody = (
        <Box sx={[styles.body, styles.mobile.root]}>
            <Text sx={[styles.titular, styles.mobile.band, { backgroundColor: titular.color }]}>{titular.text}</Text>
            <Box sx={styles.mobile.hero}>
                <Box sx={styles.mobile.photo}>{foto}</Box>
            </Box>
            <Text sx={[styles.nombre, styles.mobile.nombre]}>{hasInfoField(nombre_completo)}</Text>
            {lastSeen(styles.mobile.ls)}
            <Box sx={styles.mobile.chips}>
                {chips.map((c, i) => <Text component="span" key={i} sx={styles.mobile.chip}>{c}</Text>)}
            </Box>
            <Box sx={styles.mobile.datos}>
                <Text sx={styles.lbl}>Señas particulares:</Text>
                <Text sx={[styles.val, styles.mobile.clamp2]}>{senasText}</Text>
                <Text sx={[styles.lbl, styles.mobile.lbl2]}>Vestimenta:</Text>
                <Text sx={[styles.val, styles.mobile.clamp2]}>{vestimentaText}</Text>
                <Box sx={styles.mobile.expand}>
                    <ExpandBtn IMG_SRC={VIEW} />
                </Box>
            </Box>
            <Box sx={styles.mobile.contact}>
                <Box>
                    <Text sx={styles.mobile.t}>¿Tienes información?</Text>
                    <Text sx={styles.mobile.ph}>{CONTACT_INFO.PHONE}</Text>
                    <Text sx={styles.mobile.em}>{CONTACT_INFO.EMAIL}</Text>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box component="article" sx={styles.tableStyles}>
            <Box
                component="main"
                aria-label="Cuerpo de la cédula"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleViewImage}
                sx={{
                    ...styles.mainContainer,
                    ...(isHovered ? styles.mainContainerHovered : styles.mainContainerNormal)
                }}
            >
                <img src={MARCA_AGUA} style={styles.watermark} alt="" aria-hidden="true" />
                {responsiveScreen ? mobileBody : desktopBody}
            </Box>

            <Modal
                open={viewImageModal}
                footer={null}
                centered
                destroyOnHidden
                closable={false}
                onCancel={() => setViewImageModal(false)}
                width={responsiveScreen ? undefined : viewSize}
                styles={{ content: responsiveScreen ? styles.modalContent : { ...styles.modalContent, ...styles.modalFit } }}
            >
                {responsiveScreen
                    ? <MissingPersonPng missingPersonData={personItem} />
                    : (
                        <div style={{ width: viewSize, height: viewSize }}>
                            <MissingPersonPng missingPersonData={personItem} viewScale={viewScale} />
                        </div>
                    )}
                <ButtonAntd
                    type="secondary"
                    shape="circle"
                    icon={<CloseOutlined style={styles.closeIconButton} />}
                    onClick={() => setViewImageModal(false)}
                    style={{ ...styles.closeIcon, bottom: responsiveScreen ? -100 : 0 }}
                />
            </Modal>

            <MissingPersonCardFooter
                missingPersonData={personItem}
                onPressReport={onPressReport}
                onPressShare={onPressShare}
            />
        </Box>
    );
};

export default MissingPersonCard;
